import { z } from 'zod';

/**
 * Phase 27 v1.6 — Wallet prepago empresa (REQ-WALLET-01).
 *
 * Shared schemas crossing backend (WalletTopupService DTOs + PayWithSaldoService) and
 * mobile (depositar.page form validation + topup polling + checkout pay-com-saldo CTA).
 *
 * Locks the contracts from Phase 27 CONTEXT.md decisions:
 *   - D-02: WalletTopup shape (PENDENTE/PAGO/EXPIRADO/CANCELADO).
 *   - D-08: Topup request validation R$ 50-50.000 step R$ 10 (PM-4 acceptance).
 *   - D-14: PayWithSaldo request + response (paidWith='saldo', status='pago').
 *   - D-05: PublicLink response (slug + url).
 *
 * BigInt-cents convention (Phase 19 precedent): wire amounts as strings on read
 * (serialized BigInt) and integer cents on write (DTO input). Service code wraps
 * the write side as `BigInt(valorCents)` before DB. Pitfall 7 (BigInt precision).
 */

// ---------- Enums ----------

export const WalletTopupStatusSchema = z.enum([
  'PENDENTE',
  'PAGO',
  'EXPIRADO',
  'CANCELADO',
]);

export type WalletTopupStatus = z.infer<typeof WalletTopupStatusSchema>;

// ---------- Topup request (POST /carteira/wallet/topup) ----------

/**
 * Body for POST /carteira/wallet/topup.
 *
 * PM-4 acceptance criteria:
 *   - Min R$ 50,00       → 5_000 cents
 *   - Max R$ 50.000,00   → 5_000_000 cents
 *   - Step R$ 10,00      → valorCents % 1_000 === 0
 *
 * Defense-in-depth: service in plan 27-05 re-validates via explicit if/throw
 * with discriminated error codes (topup_valor_fora_intervalo /
 * topup_valor_step_invalido) so the API surface returns 422 even if a buggy
 * client bypasses the schema.
 */
export const CreateTopupRequestSchema = z
  .object({
    valorCents: z.number().int(),
  })
  .refine((v) => v.valorCents >= 500 && v.valorCents <= 5_000_000, {
    message: 'valorCents fora do intervalo permitido (500-5.000.000 cents)',
    path: ['valorCents'],
  });

export type CreateTopupRequest = z.infer<typeof CreateTopupRequestSchema>;

// ---------- Topup DTO (read shape) ----------

/**
 * Response shape for:
 *   POST /carteira/wallet/topup          (newly created — qrCode/qrCodeBase64 populated)
 *   GET  /carteira/wallet/topup/:id      (read — fields nullable depending on status)
 *
 * Nullable fields reflect migration 0015 WalletTopup column nullability:
 *   - chargeId is set only after Pagar.me createPixCharge succeeds
 *   - qrCode / qrCodeBase64 / expiraEm depend on Pagar.me response
 *   - pagoEm set only when webhook confirms (status='PAGO')
 *
 * valorCents serialized as number here (DTO boundary); BigInt wraps server-side.
 */
export const TopupDtoSchema = z.object({
  id: z.string().min(1),
  empresaId: z.string().min(1),
  valorCents: z.number().int().nonnegative(),
  chargeId: z.string().nullable(),
  status: WalletTopupStatusSchema,
  qrCode: z.string().nullable(),
  qrCodeBase64: z.string().nullable(),
  expiraEm: z.iso.datetime().nullable(),
  pagoEm: z.iso.datetime().nullable(),
  criadoEm: z.iso.datetime(),
});

export type TopupDto = z.infer<typeof TopupDtoSchema>;

// ---------- Topup creation response (POST returns this) ----------

/**
 * What POST /carteira/wallet/topup returns immediately after Pagar.me PIX
 * charge creation. Mobile depositar.page binds qrCodeBase64 to an <img> tag
 * and qrCodeText to a copy-button. expiraEm drives the polling deadline.
 */
export const TopupCreatedResponseSchema = z.object({
  topupId: z.string().min(1),
  chargeId: z.string().min(1),
  qrCodeBase64: z.string().nullable(),
  qrCodeText: z.string().nullable(),
  expiraEm: z.iso.datetime().nullable(),
});

export type TopupCreatedResponse = z.infer<typeof TopupCreatedResponseSchema>;

// ---------- Topup listing (GET /carteira/wallet/topups) ----------

export const ListTopupsQuerySchema = z.object({
  status: WalletTopupStatusSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
});

export const ListTopupsResponseSchema = z.object({
  items: z.array(TopupDtoSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});

export type ListTopupsQuery = z.infer<typeof ListTopupsQuerySchema>;
export type ListTopupsResponse = z.infer<typeof ListTopupsResponseSchema>;

// ---------- Pay-with-saldo (POST /carteira/wallet/pay-evento/:eventoId) ----------

/**
 * Body for POST /carteira/wallet/pay-evento/:eventoId.
 *
 * eventoId is URL-path-only; this schema exists for client typing convenience
 * (empty body is acceptable). The actual request body is `{}`.
 */
export const PayWithSaldoRequestSchema = z.object({
  eventoId: z.string().min(1).optional(),
});

export type PayWithSaldoRequest = z.infer<typeof PayWithSaldoRequestSchema>;

/**
 * Response on successful pay-with-saldo. paidWith is a literal 'saldo' (Pagar.me-direct
 * PIX returns 'pix' — see D-15 backward compat). Status is the resolved 'pago' string
 * (not pending — pay-with-saldo is synchronous in the conditional UPDATE flow per D-14).
 */
export const PagoComSaldoResponseSchema = z.object({
  pagamentoId: z.string().min(1),
  eventoId: z.string().min(1),
  valorCents: z.number().int().nonnegative(),
  paidWith: z.literal('saldo'),
  status: z.literal('pago'),
  pagoEm: z.iso.datetime(),
});

export type PagoComSaldoResponse = z.infer<typeof PagoComSaldoResponseSchema>;

// ---------- PublicLink (GET /eventos/:id/publicLink) ----------

/**
 * Response from GET /eventos/:id/publicLink (auth empresa-only).
 *
 * D-05: returned only when evento.status ∈ {publicado, em_andamento}; otherwise
 * the backend throws 422 `share_indisponivel_status`. URL is composed server-side
 * via PUBLIC_BASE_URL env (plan 27-03) — clients use this verbatim instead of
 * reconstructing.
 *
 * Slug min length 8 protects against accidental short slugs (nanoid(12) — 12 chars
 * URL-safe alphabet — well above the floor).
 */
export const PublicLinkResponseSchema = z.object({
  slug: z.string().min(8),
  url: z.url(),
});

export type PublicLinkResponse = z.infer<typeof PublicLinkResponseSchema>;
